import {useRef, useEffect} from 'react'
import { localesMap, langselectorItems } from '@/lib/translations/locales'
import { Headline } from '@/components/ui/headline'
import { Section } from '@/components/ui/section'
import IconCross from '@/icons/cross.svg'
import IconGlobe from '@/icons/globe.svg'
import IconFlagGermany from '@/icons/flags/germany.svg'
import IconFlagFrance from '@/icons/flags/france.svg'
import IconFlagItaly from '@/icons/flags/italy.svg'
import IconFlagNetherlands from '@/icons/flags/netherlands.svg'
import IconFlagSwitzerland from '@/icons/flags/switzerland.svg'
import IconFlagAustria from '@/icons/flags/austria.svg'
import IconFlagPoland from '@/icons/flags/poland.svg'
import IconFlagUK from '@/icons/flags/uk.svg'
import IconFlagCanada from '@/icons/flags/canada.svg'
import IconFlagRussia from '@/icons/flags/russia.svg'
import IconFlagUSA from '@/icons/flags/usa.svg'
import Link from "next/link";
import {usePathname, useSearchParams} from "next/navigation";
import {replaceLocale} from "@/lib/utils";
import {TopbarSettings} from "@/components/custom/header/settings";

const iconMap: {[key: string]: any} = {
    Global: <IconGlobe />,
    Germany: <IconFlagGermany />,
    France: <IconFlagFrance />,
    Netherlands: <IconFlagNetherlands />,
    Switzerland: <IconFlagSwitzerland />,
    Austria: <IconFlagAustria />,
    Poland: <IconFlagPoland />,
    'United Kingdom': <IconFlagUK />,
    Canada: <IconFlagCanada />,
    Italy: <IconFlagItaly />,
    Russia: <IconFlagRussia />,
    USA: <IconFlagUSA />
}

interface LanguageCountrySelectorItemProps {
    country: string;
    region: string;
    languages: string[];
    show: boolean;
}

const LanguageCountrySelectorItem = ({ country, region, languages, show }: LanguageCountrySelectorItemProps) => {
    const pathname = usePathname();
    const searchParams = useSearchParams();

    if (show) {
        return (
            <li className='languageselector__item flex flex-row gap-2'>
                <div className='icon flex-none w-6 h-6 flex items-center justify-center text-primary-foreground bg-primary rounded-full overflow-hidden'>
                    {iconMap[country]}
                </div>
                <div className='content flex-1'>
                    <Headline variant='h4' size='sm' className='normal-case tracking-normal'>
                        {country}
                    </Headline>
                    <div className='links leading-4'>
                        {languages.map((language: string) => {
                            const locale = `${language}-${region}`;
                            return <Link
                                key={language}
                                href={replaceLocale(`${searchParams ? `${pathname}?${searchParams}` : pathname}`, locale)}
                                className='text-xs text-gray-beige-dark hover:underline [&:not(:first-child)]:before:content-["|"] [&:not(:first-child)]:before:ml-1 [&:not(:first-child)]:before:mr-1'
                            >
                                {localesMap[language]}
                            </Link>
                        })}
                    </div>
                </div>
            </li>
        )
    }
}

interface LangCountrySelectorProps {
    langSelectorHeadline: string;
    langSelectorOpen: boolean;
    setLangSelectorOpen: (open: boolean) => void;
    unitSystemSwitchTranslation: {
        heading: string;
        metric: string;
        imperial: string;
        notification: string;
    },
    scopeSystemSwitchTranslation: {
        heading: string;
        internal: string;
        external: string;
    },
    language: string,
    region: string
}

const LangCountrySelector = ({ langSelectorHeadline, langSelectorOpen, setLangSelectorOpen, unitSystemSwitchTranslation, scopeSystemSwitchTranslation, language, region }: LangCountrySelectorProps) => {
    const sectionRef = useRef<HTMLDivElement>(null)

    const wrapperClasses = `section__wrapper overflow-hidden transition-all duration-700 ease-in-out ${
        langSelectorOpen ? `max-h-[var(--lang-selector-height)]` : 'max-h-0 invisible'
    }`

    useEffect(() => {
        if(sectionRef.current != null) sectionRef.current.style.setProperty('--lang-selector-height', `${sectionRef.current.scrollHeight}px`)
    }, [])

    return (
        <div ref={sectionRef} className={wrapperClasses}>
            <Section
                id='languageselector'
                name='languageselector'
                variant='gray-beige-light'
                flow={false}
                padding='sm'
                className={'gap-y-4'}
                sectionClassName='shadow-[inset_0_-10px_40px_-10px_rgba(0,0,0,0.15)]'
            >
                <div className='languageselector__headline col-span-3 flex flex-col gap-2'>
                    <Headline variant='h2' size='lg' className='font-normal'>
                        {langSelectorHeadline}
                    </Headline>
                    <TopbarSettings
                        unitSystemSwitchTranslation={unitSystemSwitchTranslation}
                        scopeSystemSwitchTranslation={scopeSystemSwitchTranslation}
                        language={language}
                        region={region}
                    />
                </div>
                <div className='languageselector__close col-span-1 lg:order-2 justify-self-end'>
                    <div
                        onClick={() => setLangSelectorOpen(false)}
                        className='icon__wraper transition-all duration-500 hover:bg-primary hover:text-primary-foreground rounded-full p-2 cursor-pointer'
                    >
                        <IconCross />
                    </div>
                </div>
                <ul className='languageselector__list gap-4 col-span-full lg:order-1 lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4'>
                    {langselectorItems.map((item) => (
                        <LanguageCountrySelectorItem key={item.id} country={item.countryName} region={item.region} languages={item.languages} show={item.showInSelector} />
                    ))}
                </ul>
            </Section>
        </div>
    )
}

export { LangCountrySelector }
